/**
 * Login page with Microsoft 365 sign-in
 */

import { useState } from 'react'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'

const LoginPage = () => {
  const { login, isLoading, isLoggingOut, deactivationError } = useAuth()
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const handleMicrosoftLogin = async () => {
    if (isLoggingIn || isLoading) return
    setIsLoggingIn(true)
    try {
      await login()
    } catch (error) {
      console.error('Login error:', error)
      toast.error('Microsoft login failed. Please try again.')
    } finally {
      setIsLoggingIn(false)
    }
  }

  const isAnyLoading = isLoading || isLoggingIn || isLoggingOut

  if (isLoading || isLoggingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
          <p className="text-sm text-gray-500">
            {isLoggingOut ? 'Signing out...' : 'Checking authentication...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel — branding ── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between bg-gradient-to-br from-red-700 via-red-600 to-red-800 p-12 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute top-1/3 -left-16 w-56 h-56 bg-white/5 rounded-full" />
        <div className="absolute -bottom-16 right-1/4 w-64 h-64 bg-white/5 rounded-full" />

        {/* Logo / brand */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
              </svg>
            </div>
            <span className="text-white font-bold text-lg tracking-wide">Antara ETP</span>
          </div>
        </div>

        {/* Center copy */}
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            HR Management<br />System
          </h1>
          <p className="text-red-100 text-lg mb-10">
            Kelola SDM Anda dengan lebih efisien dan terstruktur.
          </p>

          <div className="space-y-4">
            {[
              { icon: '📊', label: 'Penilaian Individu 360°' },
              { icon: '🗓️', label: 'Manajemen Cuti & Absensi' },
              { icon: '💰', label: 'Reimbursement & Klaim' },
              { icon: '🎓', label: 'Learning & Development' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xl">{icon}</span>
                <span className="text-red-100 text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-red-200 text-xs">
            &copy; {new Date().getFullYear()} PT Antara ETP. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Right panel — login form ── */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-500 tracking-widest uppercase">Antara ETP</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Selamat datang</h2>
              <p className="text-gray-500 mt-1 text-sm">
                Masuk menggunakan akun Microsoft 365 Anda
              </p>
            </div>

            {/* Error banner */}
            {deactivationError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-800">Akun Tidak Aktif</p>
                  <p className="text-sm text-red-700 mt-0.5">{deactivationError}</p>
                </div>
              </div>
            )}

            {/* Microsoft sign-in button */}
            <button
              onClick={handleMicrosoftLogin}
              disabled={isAnyLoading}
              className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-xl px-5 py-3.5 text-gray-700 font-medium text-sm hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                  </svg>
                  <span>Sign in with Microsoft</span>
                </>
              )}
            </button>

            {/* Domain notice */}
            <div className="mt-5 flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-amber-700">
                Hanya akun <span className="font-semibold">@antaraetp.com</span> yang dapat mengakses sistem ini.
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Butuh bantuan? Hubungi IT Support Antara ETP
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
